/* eslint-disable @typescript-eslint/no-shadow */
import { ApolloError, gql, useLazyQuery } from '@apollo/client';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { tacticFragment } from 'basics/graphql/fragments/tactic.fragments';
import { QueryOptionsType } from 'basics/types/common.types';
import { Tactic, TacticFilters } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

const GET_ALL_TACTICS_LAZY = gql`
  query GetAllTacticsQuery($limit: Int!, $offset: Int!, $filters: TacticFilters) {
    getTactics(limit: $limit, offset: $offset, filters: $filters) {
      tactics {
        ${tacticFragment}
      }
      count
    }
  }
`;

type ResultType = {
  getTactics: {
    tactics: Tactic[];
    count: number;
  };
};

const defaultTacticsValue: Tactic[] = [];

const useGetAllTacticsLazy = (
  queryOptions: QueryOptionsType = { limit: 5000, offset: 0 },
  filters: TacticFilters = {},
) => {
  const { limit, offset } = queryOptions;

  const [getAllTacticsLazy, { data, loading, error }] = useLazyQuery<ResultType>(GET_ALL_TACTICS_LAZY, {
    fetchPolicy: 'cache-and-network',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
    variables: {
      limit,
      offset,
      filters,
    },
  });

  if (error) {
    const customContext = extractApolloErrorDetails(error);
    Logger.logError({
      error,
      customContext,
      transactionName: 'getTacticsByCepIdLazy.ts - useGetAllTacticsLazy',
    });
  }

  const fetchTactics = async (
    filters: TacticFilters,
    limit = 5000,
    offset = 0,
  ): Promise<{
    data: { tactics: Tactic[]; count: number } | null;
    error: ApolloError | null;
  }> => {
    try {
      const result = await getAllTacticsLazy({
        variables: {
          filters,
          limit,
          offset,
        },
      });

      return {
        data: result.data?.getTactics || { tactics: defaultTacticsValue, count: 0 },
        error: null,
      };
    } catch (err) {
      const typedError = err as ApolloError;

      Logger.logError({
        error: typedError,
        customContext: { filters, limit, offset },
        transactionName: 'fetchTactics - useGetAllTacticsLazy',
      });

      return {
        data: null,
        error: typedError,
      };
    }
  };

  return {
    fetchTactics,
    data: data?.getTactics.tactics || defaultTacticsValue,
    count: data?.getTactics.count || 0,
    loading,
    error,
  };
};

export default useGetAllTacticsLazy;
